import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Storage } from 'aws-amplify';
import { ServiceFactory } from '../services/factory/ServiceFactory';
import { isDemoMode, demoQuotesStore, DemoQuote, demoDocumentsStore, demoBrokersStore, seedDemoBrokersIfEmpty } from '../utils/demo';
import { DemoQuotesService } from '../services/demo-quotes.service';
import { Auth } from 'aws-amplify';
import {
  FormSection,
  Input,
  Select,
  Textarea,
  Checkbox,
  Radio,
  FileInput,
  Button,
  CurrencyInput
} from '../components/ui/FormElements';
import { AnimatedSelect } from '../components/ui/AnimatedSelect';
import { useAuth } from '../contexts/AuthContext';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import ErrorHandler from '../utils/errorHandler';

// Icon components for inputs
const CompanyIcon = () => (
  <svg
    width={20}
    height={20}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g opacity={0.8}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.33398 6.66667C2.87375 6.66667 2.50065 7.03976 2.50065 7.5V15.8333C2.50065 16.2936 2.87375 16.6667 3.33398 16.6667H16.6673C17.1276 16.6667 17.5007 16.2936 17.5007 15.8333V7.5C17.5007 7.03976 17.1276 6.66667 16.6673 6.66667H3.33398ZM0.833984 7.5C0.833984 6.11929 1.95327 5 3.33398 5H16.6673C18.048 5 19.1673 6.11929 19.1673 7.5V15.8333C19.1673 17.214 18.048 18.3333 16.6673 18.3333H3.33398C1.95327 18.3333 0.833984 17.214 0.833984 15.8333V7.5Z"
        fill="#9CA3AF"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.56622 2.39825C7.03506 1.92941 7.67094 1.66602 8.33398 1.66602H11.6673C12.3304 1.66602 12.9662 1.92941 13.4351 2.39825C13.9039 2.86709 14.1673 3.50297 14.1673 4.16602V17.4993C14.1673 17.9596 13.7942 18.3327 13.334 18.3327C12.8737 18.3327 12.5006 17.9596 12.5006 17.4993V4.16602C12.5006 3.945 12.4129 3.73304 12.2566 3.57676C12.1003 3.42048 11.8883 3.33268 11.6673 3.33268H8.33398C8.11297 3.33268 7.90101 3.42048 7.74473 3.57676C7.58845 3.73304 7.50065 3.945 7.50065 4.16602V17.4993C7.50065 17.9596 7.12755 18.3327 6.66732 18.3327C6.20708 18.3327 5.83398 17.9596 5.83398 17.4993V4.16602C5.83398 3.50297 6.09738 2.86709 6.56622 2.39825Z"
        fill="#9CA3AF"
      />
    </g>
  </svg>
);

const PersonIcon = () => (
  <svg
    width={20}
    height={20}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.72 12.886a4.167 4.167 0 0 1 2.947-1.22h6.666a4.167 4.167 0 0 1 4.167 4.167v1.666a.833.833 0 1 1-1.667 0v-1.666a2.5 2.5 0 0 0-2.5-2.5H6.667a2.5 2.5 0 0 0-2.5 2.5v1.666a.833.833 0 1 1-1.667 0v-1.666a4.17 4.17 0 0 1 1.22-2.947ZM10 3.333a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Zm-4.166 2.5a4.167 4.167 0 1 1 8.333 0 4.167 4.167 0 0 1-8.333 0Z"
      opacity={0.8}
      fillRule="evenodd"
      clipRule="evenodd"
      fill="#9CA3AF"
    />
  </svg>
);

// Update the CONFIG_API_URL to use the main API URL
const API_URL = process.env.REACT_APP_ADMIN_API_URL!;

interface BrokerOption {
  id: string;
  name: string;
}

interface EmployerOption {
  id: string;
  name: string;
}

interface TpaData {
  id: string;
  name: string;
  brokers: {
    id: string;
    name: string;
    employers: {
      id: string;
      name: string;
    }[];
  }[];
}

// Add interfaces for user management
interface User {
  username: string;
  email: string;
  name: string;
  role: string;
  tpaId?: string;
  tpaName?: string;
  brokerId?: string;
  brokerName?: string;
  agency?: string;
  employerId?: string;
  employerName?: string;
  status?: string;
  enabled?: boolean;
  phoneNumber?: string;
}

interface NewUser {
  username: string;
  email: string;
  name: string;
  role: string;
  brokerId?: string;
  tpaId?: string;
  employerId?: string;
  agency?: string;
  tempPassword: string;
  phoneNumber?: string;
}

const NewQuote: React.FC = () => {
  const navigate = useNavigate();
  const { user, getIdToken } = useAuth();
  const demoMode = isDemoMode();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // API service instance
  const apiService = ServiceFactory.createApiService();
  const [apiConnected, setApiConnected] = useState<boolean | null>(null);
  const [tpaData, setTpaData] = useState<TpaData | null>(null);
  const [loadingTpaData, setLoadingTpaData] = useState(false);
  const [brokers, setBrokers] = useState<BrokerOption[]>([]);
  const [employers, setEmployers] = useState<EmployerOption[]>([]);
  const [dataError, setDataError] = useState<string | null>(null);
  
  // Add state for real broker and employer users
  const [brokerUsers, setBrokerUsers] = useState<BrokerOption[]>([]);
  const [employerUsers, setEmployerUsers] = useState<EmployerOption[]>([]);
  const [loadingBrokerUsers, setLoadingBrokerUsers] = useState(false);
  const [loadingEmployerUsers, setLoadingEmployerUsers] = useState(false);
  
  // Add state for user creation modal
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userCreationRole, setUserCreationRole] = useState<'broker' | 'employer'>('broker');
  const [newUser, setNewUser] = useState<NewUser>({
    username: '',
    email: '',
    name: '',
    role: 'broker',
    brokerId: '',
    tempPassword: '',
    phoneNumber: ''
  });
  const [userCreationError, setUserCreationError] = useState<string | null>(null);
  
  // Check API connection when component mounts
  useEffect(() => {
    const checkConnection = async () => {
      // Bypass API health check in demo mode
      if (demoMode) {
        setApiConnected(true);
        return;
      }

      const result = await ErrorHandler.handleDataFetch(
        async () => {
          const isHealthy = await apiService.checkApiHealth();
          setApiConnected(isHealthy);
          
          if (!isHealthy) {
            throw new Error('Backend server is not available');
          }
          
          return isHealthy;
        },
        {
          errorMessage: 'Unable to connect to server. Some features may not work properly.'
        }
      );
      
      if (result === null) {
        setApiConnected(false);
      }
    };

    checkConnection();
  }, [demoMode]);
  
  // Modify the useEffect to fetch broker users instead of TPA data
  useEffect(() => {
    const fetchData = async () => {
      setLoadingTpaData(true);
      setDataError(null);

      try {
        // In demo mode, use mock broker data from localStorage
        if (demoMode) {
          console.log('NewQuote: Demo mode - loading mock broker data');

          // Load demo brokers from local storage and seed if empty
          seedDemoBrokersIfEmpty();
          const demoBrokers = demoBrokersStore.readAll();
          setBrokers(demoBrokers.map(broker => ({ id: broker.id, name: broker.name })));
          setEmployers([]);
          setLoadingTpaData(false);
          return;
        }

        // Production mode - fetch from APIs
        const userTpaId = user?.tpaId;
        const isAdmin = user?.role === 'admin' || user?.role === 'kyndly_staff';

        // Admin users don't need a TPA ID - they can work across all TPAs
        if (!userTpaId && !isAdmin) {
          const errorMsg = 'Your account is not properly associated with a TPA. Please contact your administrator.';
          setDataError(errorMsg);
          ErrorHandler.showWarning(errorMsg);
          setLoadingTpaData(false);
          return;
        }

        // Fetch real broker users from Cognito
        const result = await ErrorHandler.handleDataFetch(
          () => fetchBrokerUsers(),
          {
            errorMessage: 'Failed to load broker/employer data. Please try refreshing the page.'
          }
        );

        if (result === null) {
          setDataError('Failed to load broker/employer data. Please try refreshing the page.');
        }
      } catch (error) {
        console.error('NewQuote: Error in fetchData:', error);
        setDataError('Failed to load data. Please try refreshing the page.');
      } finally {
        setLoadingTpaData(false);
      }
    };
    
    // Allow admin users to access without TPA ID, regular users with TPA ID, or demo mode
    if (demoMode || user?.tpaId || user?.role === 'admin' || user?.role === 'kyndly_staff') {
      fetchData();
    }
  }, [user?.tpaId, user?.role, demoMode]);
  
  const [formData, setFormData] = useState({
    transperraRep: user?.name || user?.username || '', // Auto-populate with logged-in user
    censusFile: [] as File[],
    censusFileName: '',
    planComparisonFile: [] as File[],
    planComparisonFileName: '',
    ichraEffectiveDate: '',
    pepm: '70', // Default value as per form
    currentFundingStrategy: '',
    targetDeductible: '',
    targetHSA: '',
    brokerId: '', // Added for broker selection
    employerId: '', // Now used for either employer ID (from admin-created users) or free-text employer name
    priorityLevel: 'standard', // Default to 'Standard'
    additionalNotes: '',
    noBroker: false, // Added for no broker option
    newToBenefits: false // Added for new to benefits checkbox
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Special validation for ICHRA Effective Date - no past dates allowed
    if (name === 'ichraEffectiveDate') {
      // Parse the date string directly to avoid timezone issues
      const selectedDateStr = value; // Format: YYYY-MM-DD
      const todayStr = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
      
      if (selectedDateStr < todayStr) {
        ErrorHandler.showWarning('ICHRA Effective Date cannot be in the past. Please select today or a future date.');
        return; // Don't update the state with invalid date
      }
    }
    
    // Regular form field updates
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Special handling for broker change is now in handleBrokerChange
    // Don't handle broker changes here anymore
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const files = Array.from(e.target.files || []);
    
    // Validate file count
    if (files.length > 3) {
      ErrorHandler.showWarning('You can only upload up to 3 files per category.');
      return;
    }
    
    // Validate individual file sizes
    const oversizedFiles = files.filter(file => !ErrorHandler.validateFileSize(file, 10));
    if (oversizedFiles.length > 0) {
      return; // ErrorHandler.validateFileSize already showed the error
    }
    
    // Validate total file size
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    const maxTotalSize = 20 * 1024 * 1024; // 20MB total
    
    if (totalSize > maxTotalSize) {
      const totalSizeMB = (totalSize / 1024 / 1024).toFixed(2);
      ErrorHandler.showWarning(`Total file size (${totalSizeMB}MB) cannot exceed 20MB per category.`);
      return;
    }
    
    // Validate file types (optional - add if needed)
    const allowedTypes = [
      'application/pdf',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      ErrorHandler.showWarning('Please upload only PDF, Excel, CSV, or Word documents.');
      return;
    }
    
    if (files.length > 0) {
      const fileNames = files.map(file => file.name).join(', ');
      const totalSizeMB = (totalSize / 1024 / 1024).toFixed(2);
      
      setFormData(prev => ({
        ...prev,
        [fieldName]: files,
        [`${fieldName}Name`]: files.length === 1 
          ? `${files[0].name} (${(files[0].size / 1024 / 1024).toFixed(2)}MB)` 
          : `${files.length} files selected (${totalSizeMB}MB total): ${fileNames}`
      }));
      
      ErrorHandler.showSuccess(`${files.length} file(s) selected successfully.`);
    }
  };

  const uploadFileToS3 = async (file: File, type: string): Promise<string> => {
    try {
      // Get TPA ID from user
      const tpaId = user?.tpaId || 'unknown';
      const brokerId = formData.brokerId || 'unknown';
      const employerId = formData.employerId || 'unknown';
      
      // Create structured path for uploads
      const path = `submissions/${tpaId}/${brokerId}/${employerId}/${type}/${Date.now()}_${file.name}`;
      
      await Storage.put(path, file, {
        contentType: file.type,
        progressCallback: (progress: any) => {
          console.log(`Uploaded: ${progress.loaded}/${progress.total}`);
        },
      });
      
      return path;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log('Submitting form data:', formData);
    
    // Validate required fields using ErrorHandler
    if (!ErrorHandler.validateRequired(formData.transperraRep, "Submitter's Name")) return;
    if (!ErrorHandler.validateRequired(formData.ichraEffectiveDate, 'ICHRA Effective Date')) return;
    if (!ErrorHandler.validateRequired(formData.brokerId, 'Broker')) return;
    
    // Always require employer ID
          // Validate employer field (now used for free-text employer name)
      if (!ErrorHandler.validateRequired(formData.employerId, 'Employer / Company Name')) return;
    
    // Validate required census file
    if (!formData.censusFile || formData.censusFile.length === 0) {
      ErrorHandler.showWarning('Census file is required. Please upload at least one census file.');
      return;
    }
    
    // Prevent double submission
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    const result = await ErrorHandler.handleFormSubmission(
      async () => {
        console.log('Starting form submission...');

        // Demo mode submission
        if (demoMode) {
          const companyName = formData.employerId || 'Unknown Company';

          // Use DemoQuotesService for localStorage persistence
          const demoRes = await DemoQuotesService.submitQuote({
            transperraRep: formData.transperraRep,
            ichraEffectiveDate: formData.ichraEffectiveDate,
            pepm: formData.pepm,
            currentFundingStrategy: formData.currentFundingStrategy,
            targetDeductible: formData.targetDeductible,
            targetHSA: formData.targetHSA,
            priorityLevel: formData.priorityLevel,
            additionalNotes: formData.additionalNotes,
            brokerId: formData.brokerId || 'demo-broker',
            employerId: formData.employerId || 'demo-employer',
            companyName,
            employerName: companyName,
            censusFile: formData.censusFile,
            planComparisonFile: formData.planComparisonFile
          });

          // For Legacy Metals, redirect to quote details with freshSubmission=true to trigger proposal generation
          if (companyName === 'Legacy Metals') {
            navigate(`/quotes/${demoRes.submissionId}?brokerId=${encodeURIComponent(formData.brokerId || 'demo-broker')}&employerId=${encodeURIComponent(formData.employerId || 'demo-employer')}&freshSubmission=true`);
          } else {
            // For other companies, redirect to quotes list
            navigate('/quotes');
          }

          return demoRes;
        }

        // Production mode submission
        // Get the authentication token before submitting the quote
        let token;
        let tpaId = user?.tpaId || '';
        
        const session = await Auth.currentSession();
        token = session.getIdToken().getJwtToken();
        console.log('Successfully retrieved auth token before form submission');
        
        // Extract custom attributes directly from token
        const payload = session.getIdToken().decodePayload();
        console.log('Token payload for debugging:', payload);
        
        // Extract TPA ID from token if not already set
        const isAdmin = user?.role === 'admin' || user?.role === 'kyndly_staff';
        
        if (payload['custom:tpa_id'] && !tpaId) {
          tpaId = payload['custom:tpa_id'];
          console.log(`Found custom:tpa_id in token: ${tpaId}`);
        } else if (!tpaId && !isAdmin) {
          throw new Error('No TPA ID found in user context or token');
        } else if (!tpaId && isAdmin) {
          // For admin users without TPA ID, use a placeholder or derive from selected broker/employer
          tpaId = 'admin-submission';
          console.log('Admin user without TPA ID, using placeholder for submission');
        }
        
        // Log the selected broker and employer
        console.log(`Selected Broker ID: ${formData.brokerId}`);
        console.log(`Selected Employer ID: ${formData.employerId}`);
        
        // Use employerId field as the company name (now contains free-text employer name)
        const companyName = formData.employerId || 'Unknown Company';
        console.log(`Company Name (Employer Name): ${companyName}`);
        
        // Prepare data for API (with actual files)
        const quoteData = {
          transperraRep: formData.transperraRep,
          contactType: '', // Removed GLI/Non-GLI logic - team will use notes instead
          ichraEffectiveDate: formData.ichraEffectiveDate,
          pepm: formData.pepm,
          currentFundingStrategy: formData.currentFundingStrategy,
          targetDeductible: formData.targetDeductible,
          targetHSA: formData.targetHSA,
          priorityLevel: formData.priorityLevel,
          additionalNotes: formData.noBroker 
            ? `${formData.additionalNotes}\n\n[NO BROKER - ${isAdmin ? 'ADMIN' : 'TPA'} DIRECT SUBMISSION]\nEmployer details should be captured in company information above.${formData.newToBenefits ? '\n\n[NEW TO BENEFITS - First time implementing employee benefits]' : ''}`
            : `${formData.additionalNotes}${formData.newToBenefits ? '\n\n[NEW TO BENEFITS - First time implementing employee benefits]' : ''}`,
          // Include the TPA ID and selected broker/employer IDs
          tpaId: tpaId,
          brokerId: formData.brokerId, // This will be TPA ID when noBroker is true
          employerId: formData.employerId, // Now contains free-text employer name or admin-created employer ID
          companyName: companyName, // Add the company name (employer name)
          employerName: companyName, // Also include as employerName for consistency
          noBroker: formData.noBroker, // Include the no broker flag
          newToBenefits: formData.newToBenefits, // Include new to benefits flag
          // Include the actual file objects
          censusFile: formData.censusFile,
          planComparisonFile: formData.planComparisonFile,
          // Include auth token to ensure it's passed to the API service
          authToken: token
        };
        
        console.log('Submitting quote data to API:', quoteData);
        
        // Submit quote using the API service
        const result = await apiService.Quote.submitQuote(quoteData);
        console.log('API response:', result);
        
        // Navigate to quotes list after successful submission
        navigate('/quotes');
        
        return result;
      },
      {
        successMessage: 'Quote submitted successfully! Redirecting to quotes list...',
        errorMessage: 'Failed to submit quote. Please check your information and try again.'
      }
    );
    
    setIsSubmitting(false);
  };

  // Target deductible options for select component
  const deductibleOptions = [
    { value: '', label: 'Select' },
    { value: '1000', label: '$1,000' },
    { value: '2000', label: '$2,000' },
    { value: '3000', label: '$3,000' },
    { value: '4000', label: '$4,000' },
    { value: '5000', label: '$5,000' },
  ];

  // HSA options for select component
  const hsaOptions = [
    { value: '', label: 'Select' },
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
  ];

  // Update employers when broker changes
  const handleBrokerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const brokerId = e.target.value;
    console.log(`NewQuote: handleBrokerChange called with brokerId ${brokerId}`);
    
    // Update the broker ID in the form data
    setFormData(prev => ({
      ...prev,
      brokerId: brokerId,
      employerId: '', // Reset employer field when broker changes
      noBroker: false // Reset no broker when selecting a broker
    }));
    
    // If no broker selected, clear employer list
    if (!brokerId) {
      console.log('NewQuote: No broker selected, clearing employers');
      setEmployers([]);
      return;
    }
    
    // Fetch real employer users for selected broker from Cognito
    fetchEmployersForBroker(brokerId);
  };

  // Handle "No Broker" checkbox change
  const handleNoBrokerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    const userTpaId = user?.tpaId;
    const isAdmin = user?.role === 'admin' || user?.role === 'kyndly_staff';
    
    if (isChecked) {
      // For admin users without TPA ID, use a placeholder; for TPA users, use their TPA ID
      const brokerIdForNoBroker = userTpaId || (isAdmin ? 'admin-direct' : 'unknown');
      
      // When "No Broker" is checked, use TPA ID or admin placeholder as broker ID
      setFormData(prev => ({
        ...prev,
        noBroker: true,
        brokerId: brokerIdForNoBroker, // Use TPA ID or admin placeholder as broker ID for S3 structure
        employerId: '', // Reset employer field
      }));
      
      // Fetch all employers (admin can see all, TPA users see their TPA's employers)
      fetchAllEmployersForTpa();
      
      console.log(`NewQuote: No Broker selected, using ${brokerIdForNoBroker} as broker ID`);
    } else {
      // When unchecked, reset broker selection
      setFormData(prev => ({
        ...prev,
        noBroker: false,
        brokerId: '',
        employerId: ''
      }));
      
      setEmployers([]);
      console.log('NewQuote: No Broker unchecked, reset broker selection');
    }
  };

  // Add a function to fetch broker users from Cognito
  const fetchBrokerUsers = async () => {
    setLoadingBrokerUsers(true);

    // In demo mode, return mock broker data
    if (isDemoMode()) {
      console.log('NewQuote: Demo mode - fetchBrokerUsers returning mock data');
      const demoBrokers = [
        { id: 'demo-broker', name: 'Demo Broker', agency: 'Demo Agency' },
        { id: 'broker_sam', name: 'ACC', agency: 'ACC Agency' },
        { id: 'broker_cart', name: 'Cart', agency: 'Cart Agency' },
        { id: 'broker_salvage', name: 'demo-broker', agency: 'Demo Agency' }
      ];
      setBrokers(demoBrokers);
      setLoadingBrokerUsers(false);
      return demoBrokers;
    }

    const result = await ErrorHandler.handleDataFetch(
      async () => {
        const userTpaId = user?.tpaId;
        const isAdmin = user?.role === 'admin' || user?.role === 'kyndly_staff';
        
        // Admin users can fetch from all TPAs, regular users need a TPA ID
        if (!userTpaId && !isAdmin) {
          throw new Error('User does not have a TPA ID in token');
        }
        
        // Get auth token
        const token = await getIdToken();
        if (!token) {
          throw new Error('Failed to get authentication token');
        }
        
        // Use the Admin API to fetch broker users
        const adminApiUrl = process.env.REACT_APP_ADMIN_API_URL!;
        const timestamp = new Date().getTime();
        
        // For admin users, fetch all brokers; for TPA users, filter by TPA ID
        const url = isAdmin 
          ? `${adminApiUrl}/api/users?role=broker&_t=${timestamp}`
          : `${adminApiUrl}/api/users?role=broker&tpaId=${userTpaId}&_t=${timestamp}`;
        
        console.log(`NewQuote: Fetching broker users from ${url}`);
        
        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          credentials: 'omit' // Don't include credentials - fixes CORS issue
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch broker users: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('NewQuote: Broker users data received:', data);
        
        // Map users to broker options
        if (data.users && Array.isArray(data.users)) {
          const brokerOptions = data.users
            .filter((u: any) => u.role === 'broker')
            .map((broker: any) => ({
              id: broker.username,
              name: broker.name || broker.username
            }));
          
          console.log(`NewQuote: Found ${brokerOptions.length} broker users`);
          setBrokers(brokerOptions); // Populate the existing brokers state
          return brokerOptions;
        } else {
          console.warn('NewQuote: No broker users found or invalid data format');
          setBrokers([]);
          return [];
        }
      },
      {
        errorMessage: 'Failed to load broker users. Please refresh the page or contact support.'
      }
    );
    
    if (result === null) {
      setBrokers([]);
    }
    
    setLoadingBrokerUsers(false);
  };

  // Add a function to fetch all employer users for the TPA (for no broker mode)
  const fetchAllEmployersForTpa = async () => {
    setLoadingEmployerUsers(true);

    // In demo mode, return mock employer data
    if (isDemoMode()) {
      console.log('NewQuote: Demo mode - fetchAllEmployersForTpa returning mock data');
      const demoEmployers = [
        { id: 'emp_sam', name: "Sam's Auto" },
        { id: 'emp_cart', name: 'Cart & Co' },
        { id: 'emp_salvage', name: 'Salvage Co' },
        { id: 'emp_demo', name: 'Demo Company' }
      ];
      setEmployers(demoEmployers);
      setLoadingEmployerUsers(false);
      return demoEmployers;
    }

    const result = await ErrorHandler.handleDataFetch(
      async () => {
        const userTpaId = user?.tpaId;
        const isAdmin = user?.role === 'admin' || user?.role === 'kyndly_staff';
        
        // Admin users can fetch from all TPAs, regular users need a TPA ID
        if (!userTpaId && !isAdmin) {
          throw new Error('Missing TPA ID');
        }
        
        // Get auth token
        const token = await getIdToken();
        if (!token) {
          throw new Error('Failed to get authentication token');
        }
        
        // Use the Admin API to fetch all employer users for the TPA
        const adminApiUrl = process.env.REACT_APP_ADMIN_API_URL!;
        const timestamp = new Date().getTime();
        
        // For admin users, fetch all employers; for TPA users, filter by TPA ID
        const url = isAdmin 
          ? `${adminApiUrl}/api/users?role=employer&_t=${timestamp}`
          : `${adminApiUrl}/api/users?role=employer&tpaId=${userTpaId}&_t=${timestamp}`;
        
        console.log(`NewQuote: Fetching all employer users from ${url}`);
        
        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          credentials: 'omit' // Don't include credentials - fixes CORS issue
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch employer users: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('NewQuote: All employer users data received:', data);
        
        // Map users to employer options
        if (data.users && Array.isArray(data.users)) {
          const employerOptions = data.users
            .filter((u: any) => u.role === 'employer')
            .map((employer: any) => ({
              id: employer.username,
              name: employer.name || employer.username
            }));
          
          console.log(`NewQuote: Found ${employerOptions.length} employer users`);
          setEmployers(employerOptions);
          return employerOptions;
        } else {
          console.warn('NewQuote: No employer users found or invalid data format');
          setEmployers([]);
          return [];
        }
      },
      {
        errorMessage: `Failed to load employer users. Please try again.`
      }
    );
    
    if (result === null) {
      setEmployers([]);
    }
    
    setLoadingEmployerUsers(false);
  };

  // Add a function to fetch employer users for a specific broker
  const fetchEmployersForBroker = async (brokerId: string) => {
    // Skip in demo mode - employers are free-text input
    if (demoMode) {
      console.log('NewQuote: Demo mode - skipping employer fetch');
      setEmployers([]);
      setLoadingEmployerUsers(false);
      return;
    }

    setLoadingEmployerUsers(true);

    const result = await ErrorHandler.handleDataFetch(
      async () => {
        const userTpaId = user?.tpaId;
        const isAdmin = user?.role === 'admin' || user?.role === 'kyndly_staff';
        
        // Admin users can fetch from all TPAs, regular users need a TPA ID
        if (!userTpaId && !isAdmin) {
          throw new Error('Missing TPA ID');
        }
        
        if (!brokerId) {
          throw new Error('Missing broker ID');
        }
        
        // Get auth token
        const token = await getIdToken();
        if (!token) {
          throw new Error('Failed to get authentication token');
        }
        
        // Use the Admin API to fetch employer users
        const adminApiUrl = process.env.REACT_APP_ADMIN_API_URL!;
        const timestamp = new Date().getTime();
        
        // For admin users, fetch all employers for the broker; for TPA users, filter by TPA ID too
        const url = isAdmin 
          ? `${adminApiUrl}/api/users?role=employer&brokerId=${encodeURIComponent(brokerId)}&_t=${timestamp}`
          : `${adminApiUrl}/api/users?role=employer&tpaId=${userTpaId}&brokerId=${encodeURIComponent(brokerId)}&_t=${timestamp}`;
        
        console.log(`NewQuote: Fetching employer users for broker ${brokerId} from ${url}`);
        
        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          credentials: 'omit' // Don't include credentials - fixes CORS issue
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch employer users: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('NewQuote: Employer users data received:', data);
        
        // Map users to employer options
        if (data.users && Array.isArray(data.users)) {
          const employerOptions = data.users
            .filter((u: any) => u.role === 'employer' && u.brokerId === brokerId)
            .map((employer: any) => ({
              id: employer.username,
              name: employer.name || employer.username
            }));
          
          console.log(`NewQuote: Found ${employerOptions.length} employer users for broker ${brokerId}`);
          setEmployers(employerOptions); // Populate the existing employers state
          return employerOptions;
        } else {
          console.warn('NewQuote: No employer users found or invalid data format');
          setEmployers([]);
          return [];
        }
      },
      {
        errorMessage: `Failed to load employer users for the selected broker. Please try selecting the broker again.`
      }
    );
    
    if (result === null) {
      setEmployers([]);
    }
    
    setLoadingEmployerUsers(false);
  };

  // Add user creation functions
  const openUserCreationDialog = (role: 'broker' | 'employer') => {
    setUserCreationRole(role);
    setNewUser({
      username: '',
      email: '',
      name: '',
      role: role,
      brokerId: role === 'employer' ? formData.brokerId : '',
      tempPassword: '',
      phoneNumber: ''
    });
    setUserCreationError(null);
    setUserDialogOpen(true);
  };

  const handleCreateUser = async () => {
    // Validate form fields using ErrorHandler
    if (!ErrorHandler.validateRequired(newUser.email.trim(), 'Email')) return;
    if (!ErrorHandler.validateEmail(newUser.email)) return;
    if (!ErrorHandler.validateRequired(newUser.name.trim(), 'Name')) return;
    if (!ErrorHandler.validateRequired(newUser.tempPassword.trim(), 'Temporary Password')) return;
    
    if (!API_URL) {
      ErrorHandler.showWarning("API URL not configured. Please contact support.");
      return;
    }
    
    const result = await ErrorHandler.handleFormSubmission(
      async () => {
        const token = await getIdToken();
        if (!token) {
          throw new Error("Authentication token not available");
        }
        
        // Create user request with proper hierarchy
        const userData = {
          username: newUser.email,
          email: newUser.email,
          name: newUser.name,
          role: newUser.role,
          tempPassword: newUser.tempPassword,
          phoneNumber: newUser.phoneNumber
        };
        
        // Add organization IDs based on role
        if (user?.tpaId) {
          (userData as any).tpaId = user.tpaId;
        }
        
        if (newUser.role === 'broker') {
          (userData as any).brokerId = newUser.email;
          // Only add agency if it has a value - this will be handled by the admin API
          if (newUser.agency && newUser.agency.trim()) {
            (userData as any).agency = newUser.agency.trim();
          }
        } else if (newUser.role === 'employer') {
          (userData as any).employerId = newUser.email;
          // Always associate with a broker - either selected broker or TPA ID for "No Broker" mode
          if (formData.noBroker) {
            // In "No Broker" mode, use TPA ID as the broker ID (TPA acts as the broker)
            const userTpaId = user?.tpaId;
            const isAdmin = user?.role === 'admin' || user?.role === 'kyndly_staff';
            const tpaIdAsBrokerId = userTpaId || (isAdmin ? 'admin-direct' : 'unknown');
            (userData as any).brokerId = tpaIdAsBrokerId;
            console.log(`NewQuote: No Broker mode - Using TPA ID (${tpaIdAsBrokerId}) as broker ID for employer user`);
          } else if (newUser.brokerId) {
            // Regular broker mode - use the selected broker
            (userData as any).brokerId = newUser.brokerId;
            console.log(`NewQuote: Regular broker mode - Using selected broker ID (${newUser.brokerId}) for employer user`);
          }
        }
        
        console.log('NewQuote: Creating user with data:', userData);
        
        const response = await fetch(`${API_URL}/api/users`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token.trim()}`
          },
          body: JSON.stringify(userData)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `API error: ${response.status}`);
        }
        
        const result = await response.json();
        console.log('NewQuote: User created successfully:', result);
        
        // Reset form and close dialog
        setNewUser({
          username: '',
          email: '',
          name: '',
          role: 'broker',
          brokerId: '',
          tempPassword: '',
          phoneNumber: ''
        });
        
        setUserDialogOpen(false);
        setUserCreationError(null);
        
        // Refresh the appropriate list and auto-select if employer was created
        if (userData.role === 'broker') {
          await fetchBrokerUsers();
        } else if (userData.role === 'employer') {
          if (formData.noBroker) {
            await fetchAllEmployersForTpa();
          } else if (formData.brokerId) {
            await fetchEmployersForBroker(formData.brokerId);
          }
          
          // Auto-populate the employer box with the newly created employer
          setFormData(prev => ({
            ...prev,
            employerId: newUser.email // Use the email as the employer ID
          }));
        }
        
        return result;
      },
      {
        successMessage: `${newUser.role === 'broker' ? 'Broker' : 'Employer'} user created successfully!`,
        errorMessage: `Failed to create ${newUser.role} user. Please check the information and try again.`
      }
    );
    
    // Clear any previous error state if successful
    if (result) {
      setUserCreationError(null);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto px-4 py-8">
      <div className="bg-brand-gradient rounded-brand p-6 mb-8 text-white shadow-brand">
        <h1 className="text-3xl font-bold mb-2">Quoting Tool Submission Form</h1>
        <p className="text-sky-100">Complete the form below to request a new ICHRA quote</p>
      </div>

      {apiConnected === false && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-brand relative mb-6" role="alert">
          <strong className="font-bold">Connection Error! </strong>
          <span className="block sm:inline">Cannot connect to the backend server. Your form submission may fail.</span>
        </div>
      )}
      
      {loadingTpaData && (
        <div className="bg-blue-50 border border-blue-300 text-blue-700 px-6 py-4 rounded-brand relative mb-6 flex items-center" role="alert">
          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>Loading TPA organization data...</span>
        </div>
      )}
      
      {dataError && (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-6 py-4 rounded-brand relative mb-6" role="alert">
          <strong className="font-bold">Data Loading Error! </strong>
          <span className="block sm:inline">{dataError}</span>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <FormSection title="Representative Information">
          <div className="grid gap-6 mb-4">
            <Input
              label="Submitter's Name"
              name="transperraRep"
              id="transperraRep"
              placeholder="Enter submitter's name"
              value={formData.transperraRep}
              onChange={handleChange}
              icon={<PersonIcon />}
              required
            />
            

          </div>
        </FormSection>

        <FormSection title="Company Information">
          <div className="grid gap-6 mb-4">

            
            {/* Broker Selection */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="block text-base font-medium text-night dark:text-white">
                  Broker
                </label>
                {!formData.noBroker && (
                  <button
                    type="button"
                    onClick={() => openUserCreationDialog('broker')}
                    className="flex items-center text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Create Broker
                  </button>
                )}
              </div>
              
              {/* No Broker Checkbox */}
              <div className="mb-3">
                <Checkbox
                  label="No Broker (TPA Direct)"
                  id="noBroker"
                  name="noBroker"
                  checked={formData.noBroker}
                  onChange={handleNoBrokerChange}
                />
                {formData.noBroker && (
                  <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                    Quote will be submitted directly by TPA without a broker intermediary.
                  </p>
                )}
              </div>
              
              <AnimatedSelect
                label=""
                name="brokerId"
                value={formData.noBroker ? '' : formData.brokerId}
                onChange={handleBrokerChange}
                options={formData.noBroker ? [] : brokers.map(broker => ({ value: broker.id, label: broker.name }))}
                required={!formData.noBroker}
                disabled={loadingTpaData || formData.noBroker}
                loading={loadingTpaData && !formData.noBroker}
                placeholder={formData.noBroker 
                  ? "No Broker Selected" 
                  : loadingTpaData 
                    ? "Loading brokers..." 
                    : "Select Broker"
                }
              />
              {!formData.noBroker && brokers.length === 0 && !loadingTpaData && (
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                  No brokers available. <button 
                    type="button"
                    onClick={() => openUserCreationDialog('broker')}
                    className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 underline"
                  >
                    Create one now
                  </button>
                </p>
              )}
            </div>
            
            {/* Employer Input - Now simplified to free text while preserving admin-created employer functionality */}
            <div className="space-y-2">
              <label className="block text-base font-medium text-night dark:text-white">
                Employer  <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="employerId"
                value={formData.employerId}
                onChange={handleChange}
                placeholder="Enter employer/company name"
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Enter the name of the company/employer for this quote submission.
              </p>
            </div>
            
            {/* Hidden Employer Selection - Preserved for future use but not displayed to users */}
            <div className="hidden">
              <div className="flex justify-between items-center">
                <label className="block text-base font-medium text-night dark:text-white">
                  Employer
                </label>
                {(formData.brokerId || formData.noBroker) && (
                  <button
                    type="button"
                    onClick={() => openUserCreationDialog('employer')}
                    className="flex items-center text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Create Employer
                  </button>
                )}
              </div>
              
              {formData.noBroker && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3 mb-3">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    <strong>No Broker Mode:</strong> You still need to select or create an employer. The TPA will handle this quote directly without broker intermediary.
                  </p>
                </div>
              )}
              
              {(formData.noBroker || !formData.noBroker) && (
                <>
                  <AnimatedSelect
                    label=""
                    name="employerId"
                    value={formData.employerId}
                    onChange={handleChange}
                    options={employers.map(employer => ({ value: employer.id, label: employer.name }))}
                    required={false}
                    disabled={loadingTpaData || (!formData.brokerId && !formData.noBroker)}
                    loading={loadingEmployerUsers}
                    placeholder={loadingTpaData 
                      ? "Loading employers..." 
                      : formData.noBroker 
                        ? "Select Employer" 
                        : formData.brokerId 
                          ? "Select Employer" 
                          : "First select a broker"
                    }
                  />
                  {formData.brokerId && employers.length === 0 && !loadingTpaData && (
                    <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                      No employers found for this broker. <button 
                        type="button"
                        onClick={() => openUserCreationDialog('employer')}
                        className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 underline"
                      >
                        Create one now
                      </button>
                    </p>
                  )}
                </>
              )}
            </div>
            
            <FileInput
              label="Census File"
              description="Upload up to 3 supported files: PDF, document, image, spreadsheet (including CSV), or other file types. Max 10MB per file, 20MB total."
              id="censusFile"
              onChange={(e) => handleFileChange(e, 'censusFile')}
              fileName={formData.censusFileName}
              multiple={true}
              required={true}
            />
            
            <div className="space-y-2">
              <FileInput
                label="Plan Comparison Information (Optional)"
                description="Upload up to 3 supported files: PDF, document, image, spreadsheet (including CSV), or other file types. Max 10MB per file, 20MB total."
                id="planComparisonFile"
                onChange={(e) => handleFileChange(e, 'planComparisonFile')}
                fileName={formData.planComparisonFileName}
                multiple={true}
                required={false}
              />
              <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">
                In order to receive a more accurate quote, please provide current plan information
              </p>
              
              <div className="mt-3">
                <Checkbox
                  label="New to Benefits?"
                  id="newToBenefits"
                  name="newToBenefits"
                  checked={formData.newToBenefits}
                  onChange={handleCheckboxChange}
                />
                {formData.newToBenefits && (
                  <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                    This will be noted in your quote request as a first-time benefits implementation.
                  </p>
                )}
              </div>
            </div>
          </div>
        </FormSection>

        <FormSection title="ICHRA Details">
          <div className="grid gap-6 mb-4">
            <Input
              label="ICHRA Effective Date"
              type="date"
              name="ichraEffectiveDate"
              id="ichraEffectiveDate"
              value={formData.ichraEffectiveDate}
              onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
              required
            />
            
            <CurrencyInput
              label="PEPM Amount (default $70)"
              name="pepm"
              id="pepm"
              value={formData.pepm}
              onChange={handleChange}
            />
            
            <Textarea
              label="Current Funding Strategy"
              name="currentFundingStrategy"
              id="currentFundingStrategy"
              rows={3}
              value={formData.currentFundingStrategy}
              onChange={handleChange}
              placeholder="How does the employer currently contribute to their group health plan?"
            />
            
            <AnimatedSelect
              label="Target Plan: Deductible"
              name="targetDeductible"
              value={formData.targetDeductible}
              onChange={handleChange}
              options={deductibleOptions}
            />
            
            <AnimatedSelect
              label="Target Plan: HSA?"
              name="targetHSA"
              value={formData.targetHSA}
              onChange={handleChange}
              options={hsaOptions}
            />
          </div>
        </FormSection>

        <FormSection title="Priority Level">
          <div className="grid gap-6 mb-4">
            <div className="space-y-2">
              <label className="block text-base font-medium text-night dark:text-white">
                Level of Priority
              </label>
              <div className="space-y-2">
                <Radio
                  label="ASAP"
                  id="asap"
                  name="priorityLevel"
                  value="asap"
                  checked={formData.priorityLevel === 'asap'}
                  onChange={handleChange}
                />
                <Radio
                  label="Standard"
                  id="standard"
                  name="priorityLevel"
                  value="standard"
                  checked={formData.priorityLevel === 'standard'}
                  onChange={handleChange}
                />
              </div>
            </div>
            
            <Textarea
              label="Additional Notes"
              name="additionalNotes"
              id="additionalNotes"
              rows={3}
              value={formData.additionalNotes}
              onChange={handleChange}
              placeholder="Any additional information or special requirements"
            />
          </div>
        </FormSection>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4 pt-4">
          <Link to="/quotes">
            <Button variant="outline" type="button">
              Cancel
            </Button>
          </Link>
          
          <Button 
            variant="outline" 
            type="button"
            onClick={() => setFormData({
              transperraRep: user?.name || user?.username || '', // Auto-populate with logged-in user
              censusFile: [],
              censusFileName: '',
              planComparisonFile: [],
              planComparisonFileName: '',
              ichraEffectiveDate: '',
              pepm: '70',
              currentFundingStrategy: '',
              targetDeductible: '',
              targetHSA: '',
              brokerId: '',
              employerId: '', // Reset employer field
              priorityLevel: 'standard',
              additionalNotes: '',
              noBroker: false,
              newToBenefits: false
            })}
          >
            Clear form
          </Button>
          
          <Button 
            variant="primary" 
            type="submit"
                            disabled={isSubmitting || (!formData.brokerId || !formData.employerId)}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Quote'}
          </Button>
        </div>
      </form>
      
      {/* User Creation Dialog */}
      {userDialogOpen && (
        <div className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center p-4 bg-night-900/50">
          <div className="relative bg-white dark:bg-night-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-night-700">
              <h3 className="text-lg font-semibold text-night dark:text-white">
                Create New {userCreationRole === 'broker' ? 'Broker' : 'Employer'}
              </h3>
              <button 
                onClick={() => setUserDialogOpen(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-night dark:text-white mb-1">Email</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => {
                      const email = e.target.value;
                      setNewUser({...newUser, email: email, username: email});
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                    placeholder="email@example.com"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    This email will be used for login.
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-night dark:text-white mb-1">Name</label>
                  <input
                    type="text"
                    value={newUser.name}
                    onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                    placeholder="Full Name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-night dark:text-white mb-1">Phone Number</label>
                  <input
                    type="tel"
                    value={newUser.phoneNumber}
                    onChange={(e) => setNewUser({...newUser, phoneNumber: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                    placeholder="Phone Number"
                  />
                </div>
                
                {/* Agency field for broker users */}
                {userCreationRole === 'broker' && (
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-1">Agency</label>
                    <input
                      type="text"
                      value={newUser.agency || ''}
                      onChange={(e) => setNewUser({...newUser, agency: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                      placeholder="Agency Name (optional)"
                    />
                  </div>
                )}
                
                {/* Show selected broker for employer creation */}
                {userCreationRole === 'employer' && formData.brokerId && (
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-1">Broker</label>
                    <input
                      type="text"
                      value={brokers.find(b => b.id === formData.brokerId)?.name || formData.brokerId}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm bg-gray-100 dark:bg-night-600 text-night dark:text-gray-400"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Employer will be associated with this broker.
                    </p>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-night dark:text-white mb-1">Temporary Password</label>
                  <input
                    type="password"
                    value={newUser.tempPassword}
                    onChange={(e) => setNewUser({...newUser, tempPassword: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                    placeholder="Temporary Password"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    User will be required to change password on first login.
                  </p>
                </div>
              </div>
              
              {userCreationError && (
                <div className="mt-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-md">
                  <p>{userCreationError}</p>
                </div>
              )}
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setUserDialogOpen(false)}
                  className="mr-2 px-4 py-2 text-sm font-medium text-night dark:text-white border border-gray-300 dark:border-night-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-night-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateUser}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-500 border border-transparent rounded-md shadow-sm hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Create {userCreationRole === 'broker' ? 'Broker' : 'Employer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewQuote; 